// Bookings — customer appointments and design consultations.
//
// Source-of-truth reference: /var/www/kitchenos/portal-standalone.html
//   list section:    line 2560 (#page-bookings)
//   list renderer:   line 6519 (window.renderBookings)
//   new modal:       line 14148 (#nb-modal)
//   edit modal:      line 14326 (#eb-modal)
//
// Backend: there are NO retailer-portal booking endpoints in
// /var/www/kitchenos-api/server.js as of this extraction. The only related
// endpoints are /api/consumer/booking (POST, public) and
// /api/consumer/bookings (GET, admin-only) — those are inbound consumer
// leads, NOT the retailer's own appointment list.
//
// This page therefore CALLS the intended retailer endpoints (see TODO
// comments below); when they 404 the page gracefully renders a friendly
// "no bookings yet" empty state. Backend wiring is a follow-up.

import { ApiError } from "@tkos/api-client";
import { confirmDialog, iconMarkup, type MiniCrmCustomer } from "@tkos/shared-ui";
import type { BookingStatus, BookingType } from "@tkos/tkos-types";

import { registerPage } from "../../shell/router";
import * as realtime from "../../shell/realtime";
// Shared mini-CRM cache the shell's `data-customer-name` click delegation reads
// synchronously. Aliased to avoid colliding with this page's local picker cache.
import { customerCache as miniCrmCache } from "../../shell/customer-cache";
import type { Page, PageContext, PageCleanup } from "../../shell/types";
import { formatAddress } from "../crm/page";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

/** Server-side shape we expect from the (future) bookings endpoint. */
interface ServerBooking {
  id: string;
  customer: string;
  type: BookingType | string;
  status: BookingStatus | string;
  /** Unix ms or ISO string — both supported. */
  when: number | string;
  /** Free-form: jsonb {line1,…} OR plain string — both handled by formatAddress. */
  address?: unknown;
  notes?: string | null;
  telephone?: string | null;
}

/** Normalised in-memory shape used by this page. */
interface Booking {
  id: string;
  customer: string;
  type: BookingType;
  status: BookingStatus;
  whenMs: number;
  address: string;
  notes: string;
  telephone: string;
}

/** Form values from the New Booking modal. */
interface NewBookingForm {
  customer: string;
  /** Real CRM contact id (UUID). Forwarded so the server links the booking to
   *  the customer and cascades it into the customer portal. "" when unknown. */
  customerId: string;
  telephone: string;
  whenMs: number;
  address: string;
  type: BookingType;
  notes: string;
}

type FilterValue = "all" | BookingStatus;

// ── Constants ────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<BookingType, string> = {
  showroom_visit: "Showroom visit",
  design_consultation: "Design consultation",
  site_visit: "Site visit",
};

const STATUS_LABELS: Record<BookingStatus, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
};

const FILTER_OPTIONS: ReadonlyArray<{ value: FilterValue; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

// TKOS icons reused by the type selector.
const ICON_SHOWROOM = iconMarkup("kitchen", { size: "sm" });
const ICON_DESIGN = iconMarkup("note", { size: "sm" });
const ICON_SITE = iconMarkup("pin", { size: "sm" });

// ── Helpers ──────────────────────────────────────────────────────────────────

const dateFmt = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

function formatWhen(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  return dateFmt.format(new Date(ms));
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Default the date/time picker to tomorrow at 10:00 (matches source behaviour). */
function defaultDateTimeLocal(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return (
    d.getFullYear() +
    "-" +
    pad(d.getMonth() + 1) +
    "-" +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    ":" +
    pad(d.getMinutes())
  );
}

/** Format a unix-ms timestamp as a `datetime-local` input value (local time). */
function toDateTimeLocal(ms: number): string {
  const d = Number.isFinite(ms) ? new Date(ms) : new Date();
  return (
    d.getFullYear() +
    "-" +
    pad(d.getMonth() + 1) +
    "-" +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    ":" +
    pad(d.getMinutes())
  );
}

const VALID_TYPES = new Set<BookingType>([
  "showroom_visit",
  "design_consultation",
  "site_visit",
]);
const VALID_STATUSES = new Set<BookingStatus>([
  "pending",
  "confirmed",
  "completed",
  "cancelled",
]);

function coerceType(raw: unknown): BookingType {
  if (typeof raw === "string" && VALID_TYPES.has(raw as BookingType)) {
    return raw as BookingType;
  }
  // Tolerate the legacy localStorage labels ("Showroom visit", etc.) so older
  // demo data still renders if it ever appears in the response.
  if (typeof raw === "string") {
    const lc = raw.trim().toLowerCase();
    if (lc.startsWith("showroom")) return "showroom_visit";
    if (lc.startsWith("design")) return "design_consultation";
    if (lc.startsWith("site")) return "site_visit";
  }
  return "showroom_visit";
}

function coerceStatus(raw: unknown): BookingStatus {
  if (typeof raw === "string" && VALID_STATUSES.has(raw as BookingStatus)) {
    return raw as BookingStatus;
  }
  return "pending";
}

function coerceWhen(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  return Date.now();
}

function normaliseBooking(b: ServerBooking): Booking {
  // Use the shared formatAddress so a jsonb-shaped {line1,city,postcode,…}
  // address never renders as "[object Object]" (Bug 1 root cause).
  const addr = formatAddress(b.address);
  return {
    id: String(b.id ?? ""),
    customer: String(b.customer ?? "").trim(),
    type: coerceType(b.type),
    status: coerceStatus(b.status),
    whenMs: coerceWhen(b.when),
    address: addr === "—" ? "" : addr,
    notes: String(b.notes ?? "").trim(),
    telephone: String(b.telephone ?? "").trim(),
  };
}

/**
 * Lightweight HTML escape. Used only for static-ish list-card markup; user
 * input that goes into modal inputs uses element.value (no escaping needed).
 */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Mount ────────────────────────────────────────────────────────────────────

const bookingsPage: Page = {
  name: "bookings",
  label: "Bookings",
  icon: "",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    // Local state — all closed over by event handlers. No globals.
    const ac = new AbortController();
    let bookings: Booking[] = [];
    let filter: FilterValue = "all";
    let loadingError: string | null = null;
    let isLoading = true;

    // ── DOM scaffold ────────────────────────────────────────────────────────

    const root = document.createElement("section");
    root.className = "bookings-page";
    root.innerHTML = `
      <header class="bookings-header">
        <h1>Bookings</h1>
        <p>Customer appointments and design consultations &mdash; confirm, edit, reschedule or cancel.</p>
      </header>

      <div class="bookings-toolbar">
        <select class="bookings-filter" data-role="filter" aria-label="Filter bookings by status">
          ${FILTER_OPTIONS.map(
            (o) => `<option value="${o.value}">${esc(o.label)}</option>`,
          ).join("")}
        </select>
        <span class="bookings-toolbar-spacer"></span>
        <button class="bookings-new-btn" type="button" data-role="new">+ New booking</button>
      </div>

      <div class="bookings-list" data-role="list" aria-live="polite"></div>
    `;
    container.appendChild(root);

    const filterEl = root.querySelector<HTMLSelectElement>("[data-role='filter']")!;
    const listEl = root.querySelector<HTMLDivElement>("[data-role='list']")!;
    const newBtn = root.querySelector<HTMLButtonElement>("[data-role='new']")!;

    // ── Modal ───────────────────────────────────────────────────────────────

    const dialog = document.createElement("dialog");
    dialog.className = "bookings-modal";
    dialog.innerHTML = `
      <form method="dialog" class="bookings-modal__form" data-role="form" novalidate>
        <header class="bookings-modal__head">
          <h2 data-role="modal-title">New booking</h2>
          <button class="bookings-modal__close" type="button" data-role="close" aria-label="Close">&times;</button>
        </header>
        <div class="bookings-modal__body">
          <div class="bookings-field bookings-picker" data-field="customer" data-role="customer-picker">
            <label for="bk-customer">Customer name</label>
            <div class="bookings-picker__search-wrap" data-role="customer-picker-search-wrap">
              <input id="bk-customer"
                     name="customer"
                     type="text"
                     class="bookings-picker__search"
                     data-role="customer-picker-search"
                     autocomplete="off"
                     role="combobox"
                     aria-autocomplete="list"
                     aria-expanded="false"
                     placeholder="Search customers by name…" />
              <div class="bookings-picker__results"
                   role="listbox"
                   data-role="customer-picker-results"
                   hidden></div>
              <div class="bookings-picker__hint" data-role="customer-picker-hint"></div>
            </div>
            <div class="bookings-picker__selected" data-role="customer-picker-selected" hidden>
              <span class="bookings-picker__chip" data-role="customer-picker-chip"></span>
              <button type="button" class="bookings-picker__clear" data-role="customer-picker-clear">Change</button>
            </div>
            <span class="bookings-field__error">Select an existing customer from the dropdown.</span>
          </div>
          <div class="bookings-field">
            <label for="bk-tel">Telephone number <span class="bookings-field__hint">(optional)</span></label>
            <input id="bk-tel" name="telephone" type="tel" autocomplete="tel" placeholder="e.g. 07700 900123" />
          </div>
          <div class="bookings-field">
            <label for="bk-when">Date &amp; time</label>
            <input id="bk-when" name="when" type="datetime-local" required />
          </div>
          <div class="bookings-field">
            <label for="bk-address">Address <span class="bookings-field__hint">(important for site visits)</span></label>
            <input id="bk-address" name="address" type="text" autocomplete="street-address" placeholder="e.g. 42 Elm Road, Harrogate, HG1 1AA" />
          </div>
          <div class="bookings-field">
            <label>Booking type</label>
            <div class="bookings-type-grid" role="radiogroup" aria-label="Booking type">
              <button type="button" class="bookings-type-btn" data-type="showroom_visit" role="radio" aria-checked="true">
                ${ICON_SHOWROOM}
                Showroom visit
              </button>
              <button type="button" class="bookings-type-btn" data-type="design_consultation" role="radio" aria-checked="false">
                ${ICON_DESIGN}
                Design consultation
              </button>
              <button type="button" class="bookings-type-btn" data-type="site_visit" role="radio" aria-checked="false">
                ${ICON_SITE}
                Site visit
              </button>
            </div>
          </div>
          <div class="bookings-field">
            <label for="bk-notes">Notes <span class="bookings-field__hint">(optional)</span></label>
            <input id="bk-notes" name="notes" type="text" placeholder="Any additional details..." />
          </div>
        </div>
        <footer class="bookings-modal__footer">
          <button type="button" class="bookings-btn bookings-btn--danger" data-role="delete" hidden>Delete booking</button>
          <span class="bookings-modal__footer-spacer"></span>
          <button type="button" class="bookings-btn" data-role="cancel">Cancel</button>
          <button type="submit" class="bookings-btn bookings-btn--primary" data-role="save">Create booking</button>
        </footer>
      </form>
    `;
    root.appendChild(dialog);

    const formEl = dialog.querySelector<HTMLFormElement>("[data-role='form']")!;
    const customerField = dialog.querySelector<HTMLDivElement>("[data-field='customer']")!;
    // Customer picker — CRM-only, mirror of compose-message + calendar-event
    // autofill patterns. Required selection on submit.
    interface BkContact { id: string; name: string }
    const customerSearch = dialog.querySelector<HTMLInputElement>(
      "[data-role='customer-picker-search']",
    )!;
    const customerSearchWrap = dialog.querySelector<HTMLDivElement>(
      "[data-role='customer-picker-search-wrap']",
    )!;
    const customerResults = dialog.querySelector<HTMLDivElement>(
      "[data-role='customer-picker-results']",
    )!;
    const customerSelectedEl = dialog.querySelector<HTMLDivElement>(
      "[data-role='customer-picker-selected']",
    )!;
    const customerChip = dialog.querySelector<HTMLSpanElement>(
      "[data-role='customer-picker-chip']",
    )!;
    const customerClearBtn = dialog.querySelector<HTMLButtonElement>(
      "[data-role='customer-picker-clear']",
    )!;
    const customerHint = dialog.querySelector<HTMLDivElement>(
      "[data-role='customer-picker-hint']",
    )!;
    let customerCache: BkContact[] | null = null;
    let customerSelection: BkContact | null = null;
    let customerHighlight = -1;

    const telInput = dialog.querySelector<HTMLInputElement>("#bk-tel")!;
    const whenInput = dialog.querySelector<HTMLInputElement>("#bk-when")!;
    const addressInput = dialog.querySelector<HTMLInputElement>("#bk-address")!;
    const notesInput = dialog.querySelector<HTMLInputElement>("#bk-notes")!;
    const typeBtns = Array.from(
      dialog.querySelectorAll<HTMLButtonElement>(".bookings-type-btn"),
    );
    const saveBtn = dialog.querySelector<HTMLButtonElement>("[data-role='save']")!;
    const cancelBtn = dialog.querySelector<HTMLButtonElement>("[data-role='cancel']")!;
    const closeBtn = dialog.querySelector<HTMLButtonElement>("[data-role='close']")!;
    const titleEl = dialog.querySelector<HTMLHeadingElement>("[data-role='modal-title']")!;
    const deleteModalBtn = dialog.querySelector<HTMLButtonElement>("[data-role='delete']")!;

    let selectedType: BookingType = "showroom_visit";
    // null = creating a new booking; otherwise the id of the booking being edited.
    let editingId: string | null = null;

    function selectType(t: BookingType): void {
      selectedType = t;
      for (const btn of typeBtns) {
        const active = btn.dataset.type === t;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-checked", active ? "true" : "false");
      }
    }

    function resetForm(): void {
      clearCustomerSelection();
      telInput.value = "";
      addressInput.value = "";
      notesInput.value = "";
      whenInput.value = defaultDateTimeLocal();
      customerField.classList.remove("bookings-field--invalid");
      selectType("showroom_visit");
    }

    /** Reflect create-vs-edit mode in the modal chrome (title, save label,
     *  delete-button visibility). */
    function applyModalMode(): void {
      const editing = editingId != null;
      titleEl.textContent = editing ? "Edit booking" : "New booking";
      saveBtn.textContent = editing ? "Save changes" : "Create booking";
      deleteModalBtn.hidden = !editing;
    }

    function showDialog(): void {
      // Use showModal for the standard backdrop + ESC-to-close behaviour.
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        // Older browsers fallback (very unlikely in our target environment).
        dialog.setAttribute("open", "");
      }
    }

    function openModal(): void {
      editingId = null;
      resetForm();
      applyModalMode();
      showDialog();
      window.setTimeout(() => customerSearch.focus(), 60);
      void refreshCustomerCache();
    }

    /** Open the modal pre-filled to edit an existing booking. */
    function openEditModal(b: Booking): void {
      resetForm();
      editingId = b.id;
      // Pre-select the existing customer. We only have the name on the booking
      // (no CRM id), which is all the PUT needs — submit sends the name string.
      selectCustomer({ id: `existing:${b.id}`, name: b.customer || "" });
      telInput.value = b.telephone;
      whenInput.value = toDateTimeLocal(b.whenMs);
      addressInput.value = b.address;
      notesInput.value = b.notes;
      selectType(b.type);
      applyModalMode();
      showDialog();
      // Keep the customer cache warm so "Change" works without a flash.
      void refreshCustomerCache();
    }

    function closeModal(): void {
      if (dialog.open) dialog.close();
    }

    // ── Customer picker wiring (CRM-only, mirror of calendar event modal) ──

    function setCustomerHint(text: string): void {
      customerHint.textContent = text;
    }

    function escPickerAttr(s: string): string {
      return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    function selectCustomer(c: BkContact): void {
      customerSelection = c;
      customerSearchWrap.hidden = true;
      customerResults.hidden = true;
      customerSearch.setAttribute("aria-expanded", "false");
      customerSelectedEl.hidden = false;
      customerChip.textContent = c.name;
      setCustomerHint("");
      customerField.classList.remove("bookings-field--invalid");
    }

    function clearCustomerSelection(): void {
      customerSelection = null;
      customerSearch.value = "";
      customerSearchWrap.hidden = false;
      customerSelectedEl.hidden = true;
      customerResults.hidden = true;
      customerResults.innerHTML = "";
      customerSearch.setAttribute("aria-expanded", "false");
      setCustomerHint("");
      customerHighlight = -1;
    }

    function renderCustomerResults(query: string): void {
      const list = customerCache ?? [];
      const q = query.trim().toLowerCase();

      if (!q && document.activeElement !== customerSearch) {
        customerResults.innerHTML = "";
        customerResults.hidden = true;
        customerSearch.setAttribute("aria-expanded", "false");
        setCustomerHint(
          list.length === 0
            ? "No customers yet — add one in the CRM first."
            : `${list.length} customer${list.length === 1 ? "" : "s"} available — start typing to search.`,
        );
        customerHighlight = -1;
        return;
      }

      const matches = q
        ? list.filter((c) => c.name.toLowerCase().includes(q))
        : list;
      const visible = matches.slice(0, 20);

      if (visible.length === 0) {
        customerResults.innerHTML = "";
        customerResults.hidden = true;
        customerSearch.setAttribute("aria-expanded", "false");
        setCustomerHint(
          list.length === 0
            ? "No customers yet — add one in the CRM first."
            : "No matches — try a different name.",
        );
        customerHighlight = -1;
        return;
      }

      customerHighlight = 0;
      customerResults.innerHTML = visible
        .map((c, i) => {
          const safeName = escPickerAttr(c.name);
          return `
            <button type="button" class="bookings-picker__option${i === customerHighlight ? " is-active" : ""}"
                    role="option" data-cid="${escPickerAttr(c.id)}"
                    aria-selected="${i === customerHighlight ? "true" : "false"}">
              <span class="bookings-picker__option-name">${safeName}</span>
            </button>
          `;
        })
        .join("");
      customerResults.hidden = false;
      customerSearch.setAttribute("aria-expanded", "true");
      setCustomerHint(
        matches.length > visible.length
          ? `Showing ${visible.length} of ${matches.length} — keep typing to narrow.`
          : "",
      );
    }

    /** Raw /crm/contacts row. Phone/email arrive already masked server-side
     *  (DEFRAUD-SACRED), so they're safe to surface in the mini-CRM popup. */
    interface WireCustomer {
      id?: string;
      name?: string;
      stage?: string;
      project?: string;
      phone?: string;
      email?: string;
      score?: number;
    }

    /** Map a /crm/contacts row into the shared mini-CRM popup shape so the
     *  shell's `data-customer-name` click delegation can resolve it on the
     *  FIRST click — mirrors leadToMiniCrm / contactToMiniCrm on the CRM and
     *  Contacts pages, which warm the same cache as they load. */
    function toMiniCrm(r: WireCustomer): MiniCrmCustomer | null {
      const id = String(r.id ?? "").trim();
      const name = String(r.name ?? "").trim();
      if (!id || !name) return null;
      const m: MiniCrmCustomer = { id, name, focusId: id };
      if (r.stage) m.stage = r.stage;
      if (r.project) m.project = r.project;
      if (r.phone) m.phone = r.phone;
      if (r.email) m.email = r.email;
      if (typeof r.score === "number") m.score = r.score;
      return m;
    }

    /** Fetch the retailer's CRM contacts and merge them into the shared
     *  mini-CRM cache. Called on page load (so the very first customer-name
     *  click resolves) and again whenever the picker refreshes. Throws on
     *  network error so callers can react (picker shows a hint; the load-time
     *  warm just logs). */
    async function warmMiniCrmCache(): Promise<WireCustomer[]> {
      const resp = await ctx.api<{ contacts?: WireCustomer[] }>(
        "/crm/contacts",
        { signal: ac.signal },
      );
      const rows = Array.isArray(resp?.contacts) ? resp.contacts : [];
      const mini = rows
        .map(toMiniCrm)
        .filter((m): m is MiniCrmCustomer => m !== null);
      miniCrmCache.merge(mini);
      return rows;
    }

    async function refreshCustomerCache(): Promise<void> {
      setCustomerHint("Loading…");
      try {
        const rows = await warmMiniCrmCache();
        const list = rows
          .map((r): BkContact => ({
            id: String(r.id ?? "").trim(),
            name: String(r.name ?? "").trim(),
          }))
          .filter((r) => r.id && r.name);
        customerCache = list;
        if (list.length === 1 && !customerSelection) {
          const only = list[0];
          if (only) {
            selectCustomer(only);
            return;
          }
        }
        renderCustomerResults(customerSearch.value);
      } catch (err) {
        if (!isAbortError(err)) {
          setCustomerHint("Could not load customers. Try again shortly.");
        }
      }
    }

    function applyCustomerHighlight(els: NodeListOf<HTMLButtonElement>): void {
      els.forEach((el, i) => {
        el.classList.toggle("is-active", i === customerHighlight);
        el.setAttribute("aria-selected", i === customerHighlight ? "true" : "false");
        if (i === customerHighlight) el.scrollIntoView({ block: "nearest" });
      });
    }

    customerSearch.addEventListener("input", () => {
      renderCustomerResults(customerSearch.value);
    });
    customerSearch.addEventListener("focus", () => {
      renderCustomerResults(customerSearch.value);
    });
    customerSearch.addEventListener("keydown", (e: KeyboardEvent) => {
      const optionEls = customerResults.querySelectorAll<HTMLButtonElement>(".bookings-picker__option");
      if (optionEls.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        customerHighlight = (customerHighlight + 1) % optionEls.length;
        applyCustomerHighlight(optionEls);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        customerHighlight = (customerHighlight - 1 + optionEls.length) % optionEls.length;
        applyCustomerHighlight(optionEls);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const chosen = optionEls[customerHighlight] ?? optionEls[0];
        chosen?.click();
      } else if (e.key === "Escape") {
        if (!customerResults.hidden) {
          e.preventDefault();
          customerResults.hidden = true;
          customerSearch.setAttribute("aria-expanded", "false");
        }
      }
    });
    customerResults.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(".bookings-picker__option");
      if (!btn) return;
      const cid = btn.dataset["cid"];
      const match = (customerCache ?? []).find((c) => c.id === cid);
      if (match) selectCustomer(match);
    });
    customerClearBtn.addEventListener("click", () => {
      clearCustomerSelection();
      setTimeout(() => customerSearch.focus(), 0);
    });
    dialog.addEventListener("click", (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest(".bookings-picker")) return;
      if (!customerResults.hidden) {
        customerResults.hidden = true;
        customerSearch.setAttribute("aria-expanded", "false");
      }
    });

    // ── Render ──────────────────────────────────────────────────────────────

    function renderList(): void {
      if (isLoading) {
        listEl.innerHTML = `<div class="bookings-state">Loading bookings…</div>`;
        return;
      }
      if (loadingError) {
        listEl.innerHTML = `<div class="bookings-state bookings-state--error">${esc(loadingError)}</div>`;
        return;
      }

      const filtered =
        filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

      if (filtered.length === 0) {
        listEl.innerHTML = `<div class="bookings-state">No bookings yet. Click <strong>+ New booking</strong> to add one.</div>`;
        return;
      }

      // Sort soonest-first, mirroring the source-of-truth.
      const sorted = filtered.slice().sort((a, b) => a.whenMs - b.whenMs);

      listEl.innerHTML = sorted.map(renderCard).join("");
    }

    function renderCard(b: Booking): string {
      const typeLabel = TYPE_LABELS[b.type];
      const statusLabel = STATUS_LABELS[b.status];
      const when = formatWhen(b.whenMs);

      const addressHtml = b.address
        ? `<div class="bookings-card__address">
             ${iconMarkup("pin", { size: "sm" })}
             <span>${esc(b.address)}</span>
           </div>`
        : "";

      const notesHtml = b.notes
        ? `<div class="bookings-card__notes">${esc(b.notes)}</div>`
        : "";

      const cancelDisabled = b.status === "cancelled" || b.status === "completed";

      return `
        <article class="bookings-card" data-id="${esc(b.id)}" data-status="${b.status}">
          <span class="bookings-card__bar" aria-hidden="true"></span>
          <div class="bookings-card__body">
            <div class="bookings-card__head">
              <span${b.customer ? ` data-customer-name="${esc(b.customer)}" style="cursor:pointer"` : ""}>${esc(b.customer || "Unnamed customer")}</span>
              <span class="bookings-card__type">· ${esc(typeLabel)}</span>
            </div>
            <div class="bookings-card__meta">
              <span>${esc(when)}</span>
              <span class="bookings-badge" data-status="${b.status}">${esc(statusLabel)}</span>
            </div>
            ${addressHtml}
            ${notesHtml}
          </div>
          <div class="bookings-card__actions">
            ${
              b.status === "pending"
                ? `<button type="button" class="bookings-action bookings-action--confirm" data-action="confirm" data-id="${esc(b.id)}">Confirm</button>`
                : ""
            }
            <button type="button" class="bookings-action" data-action="edit" data-id="${esc(b.id)}">Edit</button>
            <button type="button" class="bookings-action" data-action="cancel" data-id="${esc(b.id)}"${cancelDisabled ? " disabled" : ""}>Cancel</button>
            <button type="button" class="bookings-action bookings-action--danger" data-action="delete" data-id="${esc(b.id)}">Delete</button>
          </div>
        </article>
      `;
    }

    // ── Backend (stubbed) ───────────────────────────────────────────────────

    async function loadBookings(): Promise<void> {
      isLoading = true;
      loadingError = null;
      renderList();

      // TODO: real endpoint — once backend ships:
      //   GET /api/portal/bookings  ->  { bookings: ServerBooking[] }
      // For now we attempt the call; if it 404s (no endpoint yet) we fall
      // back to an empty list so the page still renders cleanly.
      try {
        const resp = await ctx.api<{ bookings: ServerBooking[] }>(
          "/portal/bookings",
          { method: "GET", signal: ac.signal },
        );
        bookings = (resp?.bookings ?? []).map(normaliseBooking);
      } catch (err) {
        if (isAbortError(err)) return; // teardown — bail without rendering
        if (err instanceof ApiError && err.status === 404) {
          // Endpoint not yet implemented — show empty state, not an error.
          console.info(
            "[bookings] /api/portal/bookings not implemented yet; rendering empty state.",
          );
          bookings = [];
        } else {
          console.error("[bookings] load failed", err);
          loadingError = "Could not load bookings. Please try again.";
          ctx.showToast("Could not load bookings", "error");
        }
      } finally {
        isLoading = false;
        renderList();
      }
    }

    async function createBooking(form: NewBookingForm): Promise<void> {
      // TODO: real endpoint — once backend ships:
      //   POST /api/portal/bookings  body: { customer, type, status, when, address, notes, telephone }
      const optimistic: Booking = {
        id: `tmp-${Date.now()}`,
        customer: form.customer,
        type: form.type,
        status: "pending",
        whenMs: form.whenMs,
        address: form.address,
        notes: form.notes,
        telephone: form.telephone,
      };

      try {
        const resp = await ctx.api<{ booking: ServerBooking }>(
          "/portal/bookings",
          {
            method: "POST",
            signal: ac.signal,
            body: JSON.stringify({
              customer: form.customer,
              customerId: form.customerId,
              type: form.type,
              status: "pending",
              when: form.whenMs,
              address: form.address,
              notes: form.notes,
              telephone: form.telephone,
            }),
          },
        );
        if (resp?.booking) {
          bookings.push(normaliseBooking(resp.booking));
        } else {
          bookings.push(optimistic);
        }
        ctx.showToast("Booking created", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        if (err instanceof ApiError && err.status === 404) {
          // No backend yet — treat as success so the UX still shows feedback.
          bookings.push(optimistic);
          console.info(
            "[bookings] POST /api/portal/bookings not implemented; storing optimistically in-memory.",
          );
          ctx.showToast("Booking created (local only — backend pending)", "info");
        } else {
          console.error("[bookings] create failed", err);
          ctx.showToast("Could not create booking", "error");
          return;
        }
      }
      renderList();
    }

    async function updateBookingApi(id: string, form: NewBookingForm): Promise<boolean> {
      //   PUT /api/portal/bookings/:id  body: { customer, type, when, address, notes, telephone }
      try {
        await ctx.api<{ booking?: ServerBooking }>(
          `/portal/bookings/${encodeURIComponent(id)}`,
          {
            method: "PUT",
            signal: ac.signal,
            body: JSON.stringify({
              customer: form.customer,
              type: form.type,
              when: form.whenMs,
              address: form.address,
              notes: form.notes,
              telephone: form.telephone,
            }),
          },
        );
      } catch (err) {
        if (isAbortError(err)) return false;
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.error("[bookings] update failed", err);
          ctx.showToast("Could not save changes", "error");
          return false;
        }
        // 404 — backend not ready (e.g. optimistic/local-only booking); fall
        // through to a local update so the UX still reflects the edit.
        console.info("[bookings] PUT not implemented; updating locally only.");
      }
      const idx = bookings.findIndex((b) => b.id === id);
      if (idx >= 0) {
        const cur = bookings[idx];
        if (cur) {
          bookings[idx] = {
            ...cur,
            customer: form.customer,
            type: form.type,
            whenMs: form.whenMs,
            address: form.address,
            notes: form.notes,
            telephone: form.telephone,
          };
        }
      }
      ctx.showToast("Booking updated", "success");
      renderList();
      return true;
    }

    async function confirmBookingApi(id: string): Promise<void> {
      //   PUT /api/portal/bookings/:id  body: { status: "confirmed" }
      try {
        await ctx.api<unknown>(`/portal/bookings/${encodeURIComponent(id)}`, {
          method: "PUT",
          signal: ac.signal,
          body: JSON.stringify({ status: "confirmed" }),
        });
      } catch (err) {
        if (isAbortError(err)) return;
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.error("[bookings] confirm failed", err);
          ctx.showToast("Could not confirm booking", "error");
          return;
        }
        console.info("[bookings] PUT not implemented; confirming locally only.");
      }
      const idx = bookings.findIndex((b) => b.id === id);
      if (idx >= 0) {
        const cur = bookings[idx];
        if (cur) bookings[idx] = { ...cur, status: "confirmed" };
      }
      ctx.showToast("Booking confirmed", "success");
      renderList();
    }

    async function cancelBookingApi(id: string): Promise<void> {
      // TODO: real endpoint:
      //   PUT /api/portal/bookings/:id  body: { status: "cancelled" }
      try {
        await ctx.api<unknown>(`/portal/bookings/${encodeURIComponent(id)}`, {
          method: "PUT",
          signal: ac.signal,
          body: JSON.stringify({ status: "cancelled" }),
        });
      } catch (err) {
        if (isAbortError(err)) return;
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.error("[bookings] cancel failed", err);
          ctx.showToast("Could not cancel booking", "error");
          return;
        }
        // 404 — backend not ready, fall through to local update.
        console.info("[bookings] PUT not implemented; cancelling locally only.");
      }
      const idx = bookings.findIndex((b) => b.id === id);
      if (idx >= 0) {
        const cur = bookings[idx];
        if (cur) {
          bookings[idx] = { ...cur, status: "cancelled" };
        }
      }
      ctx.showToast("Booking cancelled", "info");
      renderList();
    }

    async function deleteBookingApi(id: string): Promise<void> {
      // TODO: real endpoint:
      //   DELETE /api/portal/bookings/:id
      try {
        await ctx.api<unknown>(`/portal/bookings/${encodeURIComponent(id)}`, {
          method: "DELETE",
          signal: ac.signal,
        });
      } catch (err) {
        if (isAbortError(err)) return;
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.error("[bookings] delete failed", err);
          ctx.showToast("Could not delete booking", "error");
          return;
        }
        console.info("[bookings] DELETE not implemented; removing locally only.");
      }
      bookings = bookings.filter((b) => b.id !== id);
      ctx.showToast("Booking deleted", "info");
      renderList();
    }

    // ── Form submit ─────────────────────────────────────────────────────────

    function handleSubmit(e: Event): void {
      e.preventDefault();
      const customer = customerSelection?.name ?? "";
      if (!customer) {
        customerField.classList.add("bookings-field--invalid");
        setCustomerHint("Select a customer from the dropdown.");
        customerSearch.focus();
        return;
      }

      const whenRaw = whenInput.value;
      const whenMs = whenRaw ? new Date(whenRaw).getTime() : Date.now() + 86_400_000 * 3;

      // Forward the real CRM contact id so the booking links to the customer
      // and syncs to their portal. The edit flow seeds a non-UUID sentinel
      // ("existing:<bookingId>") — strip it so we never send a bogus id.
      const rawCustomerId = customerSelection?.id ?? "";
      const customerId = rawCustomerId.startsWith("existing:") ? "" : rawCustomerId;

      const form: NewBookingForm = {
        customer,
        customerId,
        telephone: telInput.value.trim(),
        whenMs: Number.isFinite(whenMs) ? whenMs : Date.now() + 86_400_000 * 3,
        address: addressInput.value.trim(),
        type: selectedType,
        notes: notesInput.value.trim(),
      };

      const editing = editingId;
      saveBtn.disabled = true;
      saveBtn.textContent = editing ? "Saving…" : "Creating…";
      const op = editing
        ? updateBookingApi(editing, form)
        : createBooking(form);
      void Promise.resolve(op).finally(() => {
        saveBtn.disabled = false;
        applyModalMode();
        closeModal();
      });
    }

    // ── Wiring ──────────────────────────────────────────────────────────────

    filterEl.value = filter;
    const onFilterChange = (): void => {
      filter = (filterEl.value as FilterValue) ?? "all";
      renderList();
    };
    filterEl.addEventListener("change", onFilterChange);

    const onNewClick = (): void => openModal();
    newBtn.addEventListener("click", onNewClick);

    const onTypeBtnClick = (e: MouseEvent): void => {
      const btn = e.currentTarget as HTMLButtonElement;
      const t = btn.dataset.type as BookingType | undefined;
      if (t && VALID_TYPES.has(t)) selectType(t);
    };
    for (const btn of typeBtns) {
      btn.addEventListener("click", onTypeBtnClick);
    }

    formEl.addEventListener("submit", handleSubmit);
    cancelBtn.addEventListener("click", () => closeModal());
    closeBtn.addEventListener("click", () => closeModal());

    // In-popup delete — one smooth flow: confirm → close the popup → delete.
    // (Replaces the old clunky path of closing the popup first, then hunting
    // for the card's Delete button.)
    deleteModalBtn.addEventListener("click", () => {
      const id = editingId;
      if (!id) return;
      void (async () => {
        if (
          !(await confirmDialog("Delete this booking? This cannot be undone.", {
            title: "Delete booking",
            confirmLabel: "Delete",
            destructive: true,
          }))
        )
          return;
        closeModal();
        void deleteBookingApi(id);
      })();
    });

    // Click-outside-to-close: the dialog's backdrop receives clicks with
    // target === dialog itself. (Native <dialog> doesn't do this by default.)
    const onDialogClick = (e: MouseEvent): void => {
      if (e.target === dialog) closeModal();
    };
    dialog.addEventListener("click", onDialogClick);

    // List event delegation — Edit / Cancel / Delete.
    const onListClick = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
        "button.bookings-action",
      );
      if (!btn || btn.disabled) return;
      const action = btn.dataset.action;
      const id = btn.dataset.id;
      if (!id) return;

      if (action === "confirm") {
        void confirmBookingApi(id);
      } else if (action === "edit") {
        const booking = bookings.find((b) => b.id === id);
        if (booking) openEditModal(booking);
      } else if (action === "cancel") {
        void (async () => {
          if (!(await confirmDialog("Cancel this booking?", {
            title: "Cancel booking",
            confirmLabel: "Cancel booking",
            cancelLabel: "Keep booking",
            destructive: true,
          }))) return;
          void cancelBookingApi(id);
        })();
      } else if (action === "delete") {
        void (async () => {
          if (!(await confirmDialog("Delete this booking? This cannot be undone.", {
            title: "Delete booking",
            confirmLabel: "Delete",
            destructive: true,
          }))) return;
          void deleteBookingApi(id);
        })();
      }
    };
    listEl.addEventListener("click", onListClick);

    // ── Initial load ────────────────────────────────────────────────────────

    selectType(selectedType);
    void loadBookings();
    // Warm the shared mini-CRM cache immediately so the FIRST click on a
    // customer name in the list resolves to their record. Without this the
    // cache was only populated after visiting a CRM-adjacent page, so the
    // first click showed "No CRM record found" and only worked on a re-click.
    void warmMiniCrmCache().catch((err) => {
      if (!isAbortError(err)) {
        console.info("[bookings] could not warm mini-CRM cache", err);
      }
    });

    // ── Live updates ─────────────────────────────────────────────────────────
    // A customer confirming an appointment lands a 'confirmed' booking; refresh
    // so it appears without a manual reload during the demo. (Issue 2.)
    const offBookingUpdated = realtime.onEvent("booking.updated", () => {
      void loadBookings();
    });

    // ── Cleanup ─────────────────────────────────────────────────────────────

    return () => {
      // Abort any in-flight fetches.
      ac.abort();
      offBookingUpdated();

      // Remove all listeners we attached. (Container is wiped by the router,
      // so DOM-tree-only listeners die with the nodes — but any listener
      // attached to document/window MUST go here. We don't have any.)
      filterEl.removeEventListener("change", onFilterChange);
      newBtn.removeEventListener("click", onNewClick);
      for (const btn of typeBtns) {
        btn.removeEventListener("click", onTypeBtnClick);
      }
      formEl.removeEventListener("submit", handleSubmit);
      dialog.removeEventListener("click", onDialogClick);
      listEl.removeEventListener("click", onListClick);

      // Close the modal if it's still open at teardown.
      if (dialog.open) dialog.close();
    };
  },
};

registerPage(bookingsPage);

export default bookingsPage;
